import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';
import { CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { GRANULARITA, MESI, anniDisponibili, periodoRange } from '../../lib/venduto';

/**
 * Selettore di periodo: granularità + unità (giorno/mese/trimestre/semestre/anno).
 */
export const PeriodPicker = ({ value, onChange, archivio, testid = 'period' }) => {
  const [calOpen, setCalOpen] = React.useState(false);
  const anni = anniDisponibili(archivio);
  const range = periodoRange(value, archivio);
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Select value={value.granularita} onValueChange={(v) => set({ granularita: v })}>
          <SelectTrigger className="h-10 w-[150px]" data-testid={`${testid}-granularita`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GRANULARITA.map((g) => (
              <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {value.granularita === 'giorno' && (
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 gap-2 font-mono-numbers" data-testid={`${testid}-giorno`}>
                <CalendarDays className="w-4 h-4" />
                {value.giorno ? format(new Date(`${value.giorno}T12:00:00`), 'd MMM yyyy', { locale: it }) : 'Scegli'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                locale={it}
                selected={value.giorno ? new Date(`${value.giorno}T12:00:00`) : undefined}
                onSelect={(d) => {
                  if (d) {
                    set({ giorno: format(d, 'yyyy-MM-dd'), anno: d.getFullYear() });
                    setCalOpen(false);
                  }
                }}
              />
            </PopoverContent>
          </Popover>
        )}

        {value.granularita === 'mese' && (
          <Select value={String(value.mese)} onValueChange={(v) => set({ mese: Number(v) })}>
            <SelectTrigger className="h-10 w-[130px]" data-testid={`${testid}-mese`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MESI.map((m, i) => (
                <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {value.granularita === 'trimestre' && (
          <Select value={String(value.trimestre)} onValueChange={(v) => set({ trimestre: Number(v) })}>
            <SelectTrigger className="h-10 w-[130px]" data-testid={`${testid}-trimestre`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4].map((t) => (
                <SelectItem key={t} value={String(t)}>{t}° trimestre</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {value.granularita === 'semestre' && (
          <Select value={String(value.semestre)} onValueChange={(v) => set({ semestre: Number(v) })}>
            <SelectTrigger className="h-10 w-[130px]" data-testid={`${testid}-semestre`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2].map((s) => (
                <SelectItem key={s} value={String(s)}>{s}° semestre</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {!['tutto', 'giorno'].includes(value.granularita) && (
          <Select value={String(value.anno)} onValueChange={(v) => set({ anno: Number(v) })}>
            <SelectTrigger className="h-10 w-[100px]" data-testid={`${testid}-anno`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {anni.map((a) => (
                <SelectItem key={a} value={String(a)}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <p className="text-xs text-muted-foreground font-mono-numbers" data-testid={`${testid}-range`}>
        {range.data_inizio} → {range.data_fine}
      </p>
    </div>
  );
};

export default PeriodPicker;
